import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

/**
 * Tendina unica dell'applicazione.
 *
 * Fino a una manciata di voci la tendina del sistema operativo e' la scelta
 * migliore: e' quella che l'utente conosce, su telefono diventa la ruota
 * nativa e non costa nulla. Oltre, l'elenco non si legge piu' a colpo
 * d'occhio - si scorre - e serve una barra di ricerca; da quel punto in su
 * il componente si disegna da se'.
 *
 * Il passaggio e' automatico e deciso qui, non dal chiamante: le voci di
 * quasi tutte queste tendine arrivano dal database (sale, palazzine,
 * appartamenti, condomini), quindi nessuno sa in anticipo da che parte
 * cadra' il conto.
 */

/** Sopra questo numero di voci la tendina porta la ricerca. */
export const SOGLIA_RICERCA = 7;

/** Altezza indicativa del pannello: serve a decidere se aprirlo verso l'alto. */
const ALTEZZA_PANNELLO = 320;

/**
 * Elenco piatto delle voci, con il gruppo di provenienza.
 * Accetta sia `{ value, label }` sia `{ label, options: [...] }`.
 */
function appiattisci(options) {
    const out = [];
    (options || []).forEach(o => {
        if (!o) return;
        if (Array.isArray(o.options)) {
            o.options.forEach(v => v && out.push({ value: v.value, label: v.label, gruppo: o.label }));
        }
        else out.push({ value: o.value, label: o.label, gruppo: null });
    });
    return out;
}

/** Minuscole e senza accenti: "Citta'" si deve trovare anche scrivendo "citta". */
function normalizza(testo) {
    return String(testo == null ? '' : testo)
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');
}

export default function Select({
    value, onChange, options, className, disabled, ariaLabel, id, placeholder
}) {
    const voci = useMemo(() => appiattisci(options), [options]);

    /*
     * Il valore torna al chiamante com'era nell'elenco (numero o stringa):
     * il `<select>` nativo restituirebbe sempre una stringa, e le due strade
     * devono comportarsi allo stesso modo.
     */
    const scegli = useCallback(chiave => {
        const voce = voci.find(v => String(v.value) === String(chiave));
        if (voce) onChange(voce.value);
    }, [voci, onChange]);

    if (voci.length <= SOGLIA_RICERCA) {
        return (
            <select
                id={id}
                className={`lv-select${className ? ` ${className}` : ''}`}
                aria-label={ariaLabel}
                disabled={disabled}
                value={value == null ? '' : value}
                onChange={e => scegli(e.target.value)}
            >
                {(options || []).map((o, i) => (
                    Array.isArray(o.options)
                        ? (
                            <optgroup key={`g${i}`} label={o.label}>
                                {o.options.map(v => <option key={v.value} value={v.value}>{v.label}</option>)}
                            </optgroup>
                        )
                        : <option key={o.value} value={o.value}>{o.label}</option>
                ))}
            </select>
        );
    }

    return (
        <SelectConRicerca
            id={id}
            className={className}
            ariaLabel={ariaLabel}
            disabled={disabled}
            placeholder={placeholder}
            value={value}
            voci={voci}
            onScegli={scegli}
        />
    );
}

/** La variante lunga: casella, barra di ricerca ed elenco filtrato. */
function SelectConRicerca({ id, className, ariaLabel, disabled, placeholder, value, voci, onScegli }) {
    const [aperto, setAperto] = useState(false);
    const [query, setQuery] = useState('');
    const [evidenziata, setEvidenziata] = useState(0);
    const [versoAlto, setVersoAlto] = useState(false);

    const base = useId();
    const boxRef = useRef(null);
    const inputRef = useRef(null);
    const listaRef = useRef(null);

    const selezionata = voci.find(v => String(v.value) === String(value));

    const filtrate = useMemo(() => {
        const q = normalizza(query).trim();
        if (!q) return voci;
        // anche il nome del gruppo e' cercabile: nell'elenco delle sale
        // scrivere il condominio deve bastare a tirare fuori le sue sale
        return voci.filter(v => `${normalizza(v.label)} ${normalizza(v.gruppo)}`.includes(q));
    }, [voci, query]);

    const chiudi = useCallback(() => {
        setAperto(false);
        setQuery('');
    }, []);

    /** All'apertura la ricerca e' vuota e il cursore e' gia' dentro. */
    function apri() {
        if (disabled) return;
        const rect = boxRef.current ? boxRef.current.getBoundingClientRect() : null;
        setVersoAlto(Boolean(rect && window.innerHeight - rect.bottom < ALTEZZA_PANNELLO && rect.top > ALTEZZA_PANNELLO));
        setQuery('');
        setEvidenziata(Math.max(0, voci.findIndex(v => v === selezionata)));
        setAperto(true);
    }

    useEffect(() => {
        if (aperto && inputRef.current) inputRef.current.focus();
    }, [aperto]);

    // un clic fuori vale come "lascia perdere": nessuna scelta, tendina chiusa
    useEffect(() => {
        if (!aperto) return undefined;
        function fuori(e) {
            if (boxRef.current && !boxRef.current.contains(e.target)) chiudi();
        }
        document.addEventListener('mousedown', fuori);
        return () => document.removeEventListener('mousedown', fuori);
    }, [aperto, chiudi]);

    // la voce evidenziata dalle frecce deve restare visibile anche in fondo
    useEffect(() => {
        if (!aperto || !listaRef.current) return;
        const nodo = listaRef.current.querySelector('[data-evidenziata="1"]');
        if (nodo && nodo.scrollIntoView) nodo.scrollIntoView({ block: 'nearest' });
    }, [aperto, evidenziata, query]);

    function conferma(voce) {
        if (!voce) return;
        onScegli(voce.value);
        chiudi();
    }

    function tasti(e) {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setEvidenziata(i => Math.min(i + 1, filtrate.length - 1));
        }
        else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setEvidenziata(i => Math.max(i - 1, 0));
        }
        else if (e.key === 'Enter') {
            // la tendina vive dentro un form: Invio sceglie, non salva
            e.preventDefault();
            conferma(filtrate[evidenziata]);
        }
        else if (e.key === 'Escape') {
            /*
             * L'Escape chiude la tendina e si ferma li'. Fermare il solo
             * evento di React non basta: chi ascolta il tasto su `document`
             * - i modali - lo vedrebbe comunque e si chiuderebbe anche lui.
             */
            e.stopPropagation();
            e.nativeEvent.stopPropagation();
            chiudi();
        }
        else if (e.key === 'Tab') chiudi();
    }

    let gruppoCorrente = null;

    return (
        <div className={`lv-combo${className ? ` ${className}` : ''}`} ref={boxRef}>
            <button
                type="button"
                id={id}
                className="lv-select lv-combo-trigger"
                aria-label={ariaLabel}
                aria-haspopup="listbox"
                aria-expanded={aperto}
                disabled={disabled}
                onClick={() => (aperto ? chiudi() : apri())}
                onKeyDown={e => {
                    if (!aperto && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
                        e.preventDefault();
                        apri();
                    }
                }}
            >
                <span className={selezionata ? 'lv-combo-valore' : 'lv-combo-valore lv-combo-vuoto'}>
                    {selezionata ? selezionata.label : (placeholder || 'Seleziona…')}
                </span>
                <FontAwesomeIcon icon={['fas', 'chevron-down']} className="lv-combo-caret" />
            </button>

            {aperto && (
                <div className={`lv-combo-panel${versoAlto ? ' lv-combo-panel-su' : ''}`}>
                    <div className="lv-combo-search">
                        <FontAwesomeIcon icon={['fas', 'magnifying-glass']} />
                        <input
                            ref={inputRef}
                            type="text"
                            className="lv-combo-input"
                            placeholder="Cerca…"
                            aria-label="Cerca"
                            autoComplete="off"
                            role="combobox"
                            aria-expanded="true"
                            aria-controls={`${base}-lista`}
                            aria-activedescendant={filtrate.length ? `${base}-${evidenziata}` : undefined}
                            value={query}
                            onChange={e => { setQuery(e.target.value); setEvidenziata(0); }}
                            onKeyDown={tasti}
                        />
                    </div>

                    {filtrate.length === 0 ? (
                        <p className="lv-combo-empty">Nessuna voce trovata.</p>
                    ) : (
                        <div
                            className="lv-combo-list"
                            id={`${base}-lista`}
                            role="listbox"
                            aria-label={ariaLabel}
                            ref={listaRef}
                        >
                            {filtrate.map((v, i) => {
                                const intestazione = v.gruppo && v.gruppo !== gruppoCorrente;
                                gruppoCorrente = v.gruppo;
                                return (
                                    <React.Fragment key={`${v.gruppo || ''}-${v.value}`}>
                                        {intestazione && <p className="lv-combo-group" role="presentation">{v.gruppo}</p>}
                                        <button
                                            type="button"
                                            role="option"
                                            id={`${base}-${i}`}
                                            data-evidenziata={i === evidenziata ? '1' : '0'}
                                            aria-selected={v === selezionata}
                                            className={`lv-combo-option${i === evidenziata ? ' is-evidenziata' : ''}`}
                                            onMouseEnter={() => setEvidenziata(i)}
                                            onClick={() => conferma(v)}
                                        >
                                            {v.label}
                                        </button>
                                    </React.Fragment>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
